'use client';

import { useState, useEffect } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter, usePathname } from '@/i18n/routing';
import { useParams } from 'next/navigation';
import { X, Globe } from 'lucide-react';

const STORAGE_KEY = 'language_suggestion_dismissed';
const LANGUAGE_CHOICE_KEY = 'user_language_choice';
const SUPPORTED_LOCALES = ['en', 'pl'];
const DEFAULT_LOCALE = 'en';

function getBrowserLanguage(): string {
  if (typeof window === 'undefined') return '';
  
  // Get browser language
  const browserLang = navigator.language || '';
  
  // Extract language code (e.g., 'pl-PL' -> 'pl', 'en-US' -> 'en')
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  return langCode;
}

function shouldShowSuggestion(currentLocale: string, browserLang: string, dismissed: boolean): boolean {
  if (dismissed) return false;
  
  // Check if user has already made an explicit language choice
  if (typeof window !== 'undefined') {
    const userChoice = localStorage.getItem(LANGUAGE_CHOICE_KEY);
    // If user has chosen a language, don't show the banner
    if (userChoice) {
      return false;
    }
  }
  
  // If browser language is supported and different from current locale
  if (SUPPORTED_LOCALES.includes(browserLang) && browserLang !== currentLocale) {
    return true;
  }
  
  // If browser language is not supported and current locale is not default
  if (!SUPPORTED_LOCALES.includes(browserLang) && currentLocale !== DEFAULT_LOCALE) {
    return true;
  }
  
  return false;
}

function getSuggestedLocale(browserLang: string): string {
  // If browser language is supported, suggest it
  if (SUPPORTED_LOCALES.includes(browserLang)) {
    return browserLang;
  }
  
  // Otherwise suggest default (English)
  return DEFAULT_LOCALE;
}

export function LanguageSuggestionBanner() {
  const locale = useLocale();
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const [show, setShow] = useState(false);
  const [suggestedLocale, setSuggestedLocale] = useState<string>('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    // Check if user dismissed the suggestion
    const dismissed = localStorage.getItem(STORAGE_KEY) === 'true';
    
    // Check if user has made an explicit language choice
    const userChoice = localStorage.getItem(LANGUAGE_CHOICE_KEY);
    
    // If user has chosen a language and current locale matches, don't show banner
    if (userChoice === locale) {
      return;
    }
    
    // Get browser language
    const browserLang = getBrowserLanguage();
    
    // Check if we should show the suggestion
    if (shouldShowSuggestion(locale, browserLang, dismissed)) {
      const suggested = getSuggestedLocale(browserLang);
      setSuggestedLocale(suggested);
      setShow(true);
    }
  }, [locale]);

  const handleSwitch = () => {
    // Store that user has made an explicit language choice
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANGUAGE_CHOICE_KEY, suggestedLocale);
    }
    // @ts-expect-error -- We know the params match the route requirements
    router.replace({ pathname, params }, { locale: suggestedLocale });
    setShow(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShow(false);
  };

  if (!mounted || !show) {
    return null;
  }

  const localeNames: Record<string, string> = {
    en: 'English',
    pl: 'Polski',
  };

  const suggestedName = localeNames[suggestedLocale] || 'English';

  return (
    <div className="sticky top-[72px] z-40 w-full bg-blue-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <Globe className="flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-sm font-medium">
                {locale === 'en' && suggestedLocale === 'pl'
                  ? t('languageSuggestion.switchToPolish') || `Your browser is set to Polish. Switch to ${suggestedName}?`
                  : !SUPPORTED_LOCALES.includes(getBrowserLanguage())
                  ? t('languageSuggestion.switchToEnglish') || `Your browser language is not supported. Switch to ${suggestedName}?`
                  : t('languageSuggestion.switchLanguage', { locale: suggestedName }) || `Switch to ${suggestedName}?`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSwitch}
              className="px-4 py-1.5 bg-white text-blue-600 font-semibold rounded-md hover:bg-blue-50 transition-colors text-sm whitespace-nowrap"
            >
              {t('languageSuggestion.switch') || 'Switch'}
            </button>
            <button
              onClick={handleDismiss}
              className="p-1.5 hover:bg-blue-700 rounded-md transition-colors"
              aria-label={t('common.close') || 'Close'}
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

